import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatTabsModule } from '@angular/material/tabs';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, themeMaterial } from 'ag-grid-community';
import { lastValueFrom } from 'rxjs';
import { FilterModalNewComponent } from '../filter-modal/filter-modal.component';
import { CollectionAttributes, FilterSets } from '../models/advisor.interface';
import { Collection, FileMetadata, RowData } from '../models/collection.interface';
import { CollectionsService } from '../services/collections.service';
import { ForwardingRule, ForwardingRuleService } from '../services/forwarding-rule.service';
import { ProgressCellComponent } from '../shared/progress-cell/progress-cell.component';
import { ResearchDialogComponent } from './research-dialog/research-dialog.component';
import { RowDetailsComponent } from './row-details/row-details.component';
import { ViewResearchDialogComponent } from './view-research-dialog/view-research-dialog.component';

const LOCAL_STORAGE_KEY = 'selected_collection';
const SELECTED_COLLECTIONS_STORAGE_KEY = 'multiple_selected_collections';

@Component({
  selector: 'app-collections',
  templateUrl: './collections.component.html',
  styleUrls: ['./collections.component.scss'],
  standalone: true,
  imports: [
    AgGridAngular,
    MatAutocompleteTrigger,
    MatFormFieldModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatCardModule,
    MatIconModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    CommonModule,
    MatSidenavModule,
    MatTabsModule
  ]
})
export class CollectionsComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular; // Inject agGrid component
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger; // Reference to autocomplete trigger
  @ViewChild('rulesSidenav') rulesSidenav!: MatSidenav;
  collections: Collection[] = []; // Stores all collections (dropdown options, also where dimensions live)
  filteredCollections: Collection[] = []; // Stores filtered collections for autocomplete
  collectionInput: Collection | null = {} as Collection; // Input for autocomplete
  rowData: FileMetadata['catalogue'][] = []; // Stores file data
  filteredRowData: FileMetadata['catalogue'][] = []; // Stores file data
  colDefs: ColDef[] = this.getBaseColDefs(); // Column headers
  selectedCollection: Collection | undefined = undefined; // Active dropdown selection
  collectionSelectOptions: CollectionAttributes = {}; // For filtering via modal
  theme = themeMaterial; // Theme for ag-grid
  activeFilters: CollectionAttributes = {
    state: [],
    year: [],
    docket_number: []
  };
  forwardingRulesList: ForwardingRule[] = [];
  filteredForwardingRulesList: ForwardingRule[] = [];

  constructor(
    private collectionsService: CollectionsService,
    public dialog: MatDialog,
    private forwardingRuleService: ForwardingRuleService
  ) {}

  ngOnInit(): void {
    // Load dropdown option
    this.collectionsService.getCollections().subscribe({
      next: (data: Collection[]) => {
        // sort collections alphabetically by name
        this.collections = data.sort((a, b) => a.name.localeCompare(b.name));
        this.filteredCollections = [...this.collections]; // Initialize filtered collections
        let lastActiveCollection = this.collections.find(c => c.name === localStorage.getItem(LOCAL_STORAGE_KEY) || '');
        if (!lastActiveCollection) {
          // No session found, set default to be the first collection, sorted A-Z.
          lastActiveCollection = this.collections[0];
        }
        this.setCollection(lastActiveCollection);
        this.collectionInput = lastActiveCollection;
        // Load forwarding rules after collection is set
        this.getAllForwardingRules();
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
        if (this.agGrid.api) {
          this.agGrid.api.showNoRowsOverlay();
        }
      }
    });

    window.addEventListener('beforeunload', () => this.handleBeforeUnload());
  }

  closeRules() {
    this.rulesSidenav?.close();
  }

  openRules() {
    this.rulesSidenav?.open();
  }

  toggleRules() {
    this.rulesSidenav?.toggle();
  }

  // save the selected collection to localStorage
  setCollectionToLocalStorage(collectionName: string) {
    localStorage.setItem(LOCAL_STORAGE_KEY, collectionName);
    localStorage.setItem(SELECTED_COLLECTIONS_STORAGE_KEY, collectionName);
  }

  // Save session on page unload
  handleBeforeUnload() {
    if (this.selectedCollection) {
      console.log('Saving session on page unload: ', this.selectedCollection.name);
      localStorage.setItem('collection_id', this.selectedCollection.collection_id);
      localStorage.setItem('collection_name', this.selectedCollection.name);
    }
  }

  // Check if there are any filter options available (fetchFilterOptions populates collectionSelectOptions)
  get hasFilterOptions(): boolean {
    return Object.keys(this.collectionSelectOptions).length > 0;
  }

  // Show # of filters being applied to grid
  get filterCount(): number {
    return Object.values(this.activeFilters).flat().length;
  }

  // Find filters associated with the selected collection
  fetchFilterOptions(collection: Collection) {
    const _setToSortedArray = (set: Set<string>) => Array.from(set).sort((a, b) => a.localeCompare(b));
    const validFilters: FilterSets = {};

    collection.dimensions.forEach(dimension => {
      const standardized_name = dimension.name.replace(/\s+/g, '_').toLowerCase();
      validFilters[standardized_name] = new Set<string>();
    });

    this.collectionsService.getCollectionData(collection.collection_id).subscribe({
      next: (data: FileMetadata[]) => {
        // Populate validFilters with document metadata
        // Note: this can be computationally expensive, as N files will have N * M dimensions processed
        data.forEach((item: FileMetadata) => {
          if (!item.catalogue) return;

          // Only process keys in validFilters
          Object.entries(item.catalogue).forEach(([dimension, value]) => {
            // Remove null values to avoid having blank as a filter option
            if (value && dimension in validFilters) {
              validFilters[dimension].add(value);
            }
          });
        });

        // Convert set to sorted array
        Object.keys(validFilters).forEach(key => {
          this.collectionSelectOptions[key] = _setToSortedArray(validFilters[key]);
        });
      },
      error: error => {
        console.error('There was an error while fetching the catalogue: ', error);
      }
    });
  }

  placeholderRows(stage: FileMetadata['processing_stage'] = 'started'): FileMetadata['catalogue'] {
    return {
      collection_id: 'placeholder',
      collection_name: '',
      processing_stage: stage,
      original_document_uri: '',
      s3_output_path: '',
      company: '',
      state: '',
      year: '',
      title: ''
    };
  }

  // Update column definitions based on selected collection
  updateColDefs(collection: Collection) {
    const dimensions = collection ? collection.dimensions : [];
    const standardizedDimensions = dimensions.map(dimension => dimension.name.replace(/\s+/g, '_').toLowerCase());

    // Visible columns (the rest goes into RowDetailsComponent)
    const prioritizedColumns = ['title', 'phase', 'state', 'industry', 'company', 'year'];

    const columnWidths: Record<string, number> = {
      title: 3,
      year: 0.7
    };
    const defaultFlex = 1;

    const prioritizedColDefs = prioritizedColumns
      .filter(dim => standardizedDimensions.includes(dim))
      .map(dim => {
        const col: ColDef = {
          field: dim,
          headerName: dim
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, char => char.toUpperCase()),
          tooltipField: dim,
          flex: columnWidths[dim] || defaultFlex
        };

        if (dim === 'title') {
          col.cellRenderer = ProgressCellComponent;
        }

        return col;
      });

    this.colDefs = this.getBaseColDefs().concat(prioritizedColDefs, [
      {
        field: '',
        width: 90,
        cellRenderer: RowDetailsComponent
      }
    ]);

    if (this.agGrid.api) {
      this.agGrid.api.updateGridOptions({
        columnDefs: this.colDefs
      });
    }
  }

  // used for /research endpoints
  getBaseColDefs(): ColDef[] {
    return [
      // "s3://drift-document-store/collections/fd51a57a-e484-4bb4-ba8a-c4874cfefcd4/example.pdf"
      {
        field: 'original_document_uri',
        hide: true,
        tooltipField: 'original_document_uri'
      },
      // Legacy looked like
      // "fd51a57a-e484-4bb4-ba8a-c4874cfefcd4/drift-intermediate-json/example.json"
      // Current architecture puts them into indexables like
      // s3://drift-document-pipeline/fd51a57a-e484-4bb4-ba8a-c4874cfefcd4/drift-indexables/HONI_Appl_Exhibit B4_GSP_20210805 6859cb846e963050de17fcd119d9da4a.json

      { field: 's3_output_path', hide: true, tooltipField: 's3_output_path' },
      {
        field: 'collection_id',
        filter: true,
        hide: true,
        tooltipField: 'collection_id'
      },
      { field: 'collection_name', hide: true, tooltipField: 'collection_name' }
    ];
  }

  onQuickFilterChanged(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.agGrid.api) {
      this.agGrid.api.updateGridOptions({
        quickFilterText: input.value
      });
    }
  }

  showLoading(flag = true) {
    if (this.agGrid.api) {
      this.agGrid.api.setGridOption('loading', flag);
    }
  }

  setCollection(collection: Collection) {
    // Triggered on dropdown change (and once on load)
    // const collection = this.collections.find((c) => c.name === name);
    if (collection) {
      this.selectedCollection = collection;
      const cidBare = this.fileNameFromFileId(collection.collection_id);
      this.syncPinnedToStorage(cidBare); // restore pinned rows first

      this.fetchCollectionData(collection);
      this.updateColDefs(collection);
      this.fetchFilterOptions(collection);
      this.setCollectionToLocalStorage(collection.name);
      this.filterForwardingRules();
    }
  }

  fetchCollectionData(collection: Collection) {
    this.showLoading(true);
    const cidBare = this.fileNameFromFileId(collection.collection_id);
    const yearFrom = (d?: string) => (d || '').match(/\b(19|20)\d{2}\b/)?.[0] ?? '';

    this.collectionsService.getCollectionData(collection.collection_id).subscribe({
      next: (data: FileMetadata[]) => {
        const mapped = data.map<RowData>((item: FileMetadata) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((item as any).is_cloned_from_library === true) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cat = (item.catalogue || {}) as any;

            const title =
              cat.document_title?.trim?.() ||
              cat.title?.trim?.() ||
              this.fileNameFromUri(item.original_document_uri) ||
              (item.file_id?.includes('#') ? item.file_id.split('#')[1] : item.file_id) ||
              'Document';

            return {
              collection_id: item.collection_id,
              file_id: item.file_id,
              processing_stage: '',
              original_document_uri: item.original_document_uri ?? '',
              s3_output_path: item.s3_output_path ?? '',
              company: cat.utility_company ?? '',
              state: cat.state ?? '',
              year: yearFrom(cat.filing_date),
              title,
              collection_name: collection.name
            } as RowData;
          }

          if (item.catalogue) {
            return {
              ...item.catalogue,
              original_document_uri: item.original_document_uri ?? '',
              s3_output_path: item.s3_output_path ?? '',
              collection_id: item.collection_id,
              file_id: item.file_id,
              collection_name: collection.name,
              processing_stage: item.processing_stage ?? ''
            };
          }

          const fallbackTitle =
            this.fileNameFromFileId(item.file_id) ||
            this.fileNameFromUri(item.original_document_uri) ||
            'Upload in Progress...';

          return {
            collection_id: item.collection_id,
            file_id: item.file_id,
            processing_stage: item.processing_stage ?? '',
            original_document_uri: item.original_document_uri ?? '',
            s3_output_path: item.s3_output_path ?? '',
            company: '',
            state: '',
            year: '',
            title: fallbackTitle,
            collection_name: collection.name
          };
        });

        // clear pinned placeholders for rows that now exist/ingest/failed
        const lock = new Set(['started', 'image_extraction', 'text_extraction', 'failed']);
        for (const r of mapped) {
          if (lock.has((r.processing_stage || '').toLowerCase())) {
            this.removePlaceholderId(cidBare, r.file_id);
          }
        }

        this.rowData = mapped;
        this.filteredRowData = mapped;

        this.updateColDefs(collection);
        this.fetchFilterOptions(collection);
      },
      error: err => {
        console.error('There was an error while fetching the collections: ', err);
        this.agGrid.api.hideOverlay();
      },
      complete: () => this.showLoading(false)
    });
  }

  openResearchPromptDialog() {
    const filteredData = this.getAllRows();
    const dialogRef = this.dialog.open(ResearchDialogComponent, {
      width: '1000px',
      height: 'auto',
      data: { filteredData, collection: this.selectedCollection }
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log('The dialog was closed');
    });
  }

  getAllRows() {
    // TODO: Add Type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rows: any = [];
    if (this.agGrid.api) {
      this.agGrid.api.forEachNodeAfterFilter(node => {
        // Only add rows that have a valid original_document_uri
        if (node.data && node.data.original_document_uri.indexOf('s3') === 0) {
          rows.push(node.data);
        }
      });
    }
    return rows;
  }

  openViewResearchDialog() {
    const dialogRef = this.dialog.open(ViewResearchDialogComponent, {
      width: '1000px',
      height: 'auto'
    });

    dialogRef.afterClosed().subscribe(() => {
      console.log('The dialog was closed');
    });
  }

  // Filter collections based on user input
  filterCollections(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();

    // Filter collections based on the input value
    this.filteredCollections = this.collections.filter(collection =>
      collection.name.toLowerCase().includes(filterValue)
    );

    // If no collections match and we have a selected collection, add it to the filtered list
    if (this.filteredCollections.length === 0 && this.selectedCollection) {
      if (this.selectedCollection.name.toLowerCase().includes(filterValue)) {
        this.filteredCollections = [this.selectedCollection];
      }
    }
  }

  // Filter collections based on user Filter Modal select
  async openFilterModal(): Promise<void> {
    const dialogRef = this.dialog.open(FilterModalNewComponent, {
      width: '500px',
      data: {
        options: this.collectionSelectOptions,
        currentFilters: { ...this.activeFilters }
      }
    });

    const emittedFilters = await lastValueFrom(dialogRef.afterClosed());
    return this.updateRowDataFromFilters(emittedFilters);
  }

  updateRowDataFromFilters(filters: CollectionAttributes) {
    if (filters) {
      this.activeFilters = filters;
      let newRowData = this.rowData.filter(item => item !== undefined);

      Object.entries(filters).forEach(([filterName, stringsToMatch]) => {
        // Filter the data based on the current key and values
        newRowData = newRowData.filter(row => {
          if (!row) return false;
          if (stringsToMatch.includes(row[filterName])) {
            return true;
          }
          return false;
        });
      });
      this.filteredRowData = newRowData;
    }
  }

  // Display function for autocomplete
  displayFn(collection: Collection | null): string {
    // When a collection is selected, display its name
    return collection ? collection.name : '';
  }

  // Select the top collection from the filtered list when Enter key is pressed
  selectTopCollection(event: Event): void {
    event.preventDefault();

    // If there are filtered collections, select the first one
    if (this.filteredCollections.length > 0) {
      const topCollection = this.filteredCollections[0];
      this.setCollection(topCollection);
      this.selectedCollection = topCollection;
      this.collectionInput = topCollection;

      // Close the autocomplete panel
      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }

  clearInputText() {
    this.collectionInput = null;
    this.filteredCollections = [...this.collections];
  }

  private fileNameFromFileId(cid?: string) {
    return cid?.includes('#') ? cid.split('#')[1] : (cid ?? '');
  }

  fileNameFromUri(uri?: string): string {
    if (!uri) return '';
    const clean = uri.split('?')[0].split('#')[0];
    try {
      return decodeURIComponent(clean.split('/').pop() || '');
    } catch {
      return clean.split('/').pop() || '';
    }
  }

  private placeholdersKeyFor = (cid: string) => `uploadPlaceholders::${cid}`;

  // localStorage holds array of file_ids: ["file#name.pdf", ...]
  private loadPlaceholderIds(cidBare: string): string[] {
    try {
      return JSON.parse(localStorage.getItem(this.placeholdersKeyFor(cidBare)) || '[]');
    } catch {
      return [];
    }
  }
  private savePlaceholderIds(cidBare: string, ids: string[]) {
    localStorage.setItem(this.placeholdersKeyFor(cidBare), JSON.stringify(ids));
  }

  pinnedTopRows: { file_id: string; title: string }[] = [];
  private readonly UPLOAD_PREFIX = 'Upload in Progress - ';

  private toPinnedRows(ids: string[]) {
    return ids.map(id => ({ file_id: id, title: `${this.UPLOAD_PREFIX}${id.split('#')[1]}` }));
  }

  private syncPinnedToStorage(cidBare: string) {
    this.pinnedTopRows = this.toPinnedRows(this.loadPlaceholderIds(cidBare));
  }

  private removePlaceholderId(cidBare: string, fileId: string) {
    const keep = this.loadPlaceholderIds(cidBare).filter(id => id.toLowerCase() !== fileId.toLowerCase());
    this.savePlaceholderIds(cidBare, keep);
    this.pinnedTopRows = this.toPinnedRows(keep);
  }

  private getAllForwardingRules(): void {
    this.forwardingRuleService.getCurrentUserForwardingRules().subscribe({
      next: response => {
        this.forwardingRulesList = response;
        this.filterForwardingRules();
      },
      error: error => {
        console.error('Error fetching forwarding rules:', error);
      }
    });
  }

  private filterForwardingRules(): void {
    if (!this.selectedCollection) {
      this.filteredForwardingRulesList = this.forwardingRulesList;
      return;
    }

    this.filteredForwardingRulesList = this.forwardingRulesList.filter(
      rule => rule.target_collection_id === this.selectedCollection?.collection_id
    );
  }

  getCollectionNameById(collectionId: string): string {
    const collection = this.collections.find(c => c.collection_id === collectionId);
    return collection ? collection.name : 'Unknown Collection';
  }
}
